// Simple alasql initializer and runner
// - initAlasql(data): creates in-memory tables 'regions' and 'locations' seeded from provided data
// - runQuery(sql): executes SQL against the in-memory database and returns results or throws

import alasql from "alasql";
import getRegionsDefault from "../components/data/getregions";
import getSeedLocations from "../components/data/getSeedLocations";

let initialized = false;

export function initAlasql({ regions = null, locations = null } = {}) {
  if (initialized) return;

  // Use provided data or fall back to helpers
  const grouped = regions || getRegionsDefault();
  const locs = locations || getSeedLocations();

  // Drop any existing tables (defensive)
  try {
    alasql("DROP TABLE IF EXISTS regions");
    alasql("DROP TABLE IF EXISTS locations");
  } catch (e) {
    // ignore
  }

  // Create regions table (id, name, country)
  alasql("CREATE TABLE regions (id STRING, name STRING, country STRING)");
  const regionRows = [];
  Object.keys(grouped).forEach((country) => {
    const arr = grouped[country] || [];
    arr.forEach((r) => {
      regionRows.push({ id: r.id, name: r.name, country });
    });
  });
  if (regionRows.length) alasql.tables.regions.data = regionRows;

  // Create locations table: copy fields from seed rows
  alasql("CREATE TABLE locations");
  // Convert seed rows to plain objects, flatten arrays to JSON strings for simplicity
  const locationRows = (locs || []).map((r) => {
    const copy = { ...r };
    // ensure primitive fields only for SQL: stringify arrays and objects
    Object.keys(copy).forEach((k) => {
      const v = copy[k];
      if (Array.isArray(v) || typeof v === "object") {
        copy[k] = JSON.stringify(v);
      }
    });
    return copy;
  });
  if (locationRows.length) alasql.tables.locations.data = locationRows;

  initialized = true;
}

export async function runQuery(sql) {
  if (!initialized) initAlasql();
  try {
    const res = await alasql(sql);
    return { results: res };
  } catch (error) {
    return { error };
  }
}

// Upsert a single region row into the in-memory table
// row: { id: string, name: string, country?: string }
export function upsertRegionRow(row) {
  if (!initialized) initAlasql();
  if (!row || !row.id) return;
  const tbl = alasql.tables.regions;
  if (!tbl || !Array.isArray(tbl.data)) return;
  const data = tbl.data;
  const idx = data.findIndex((r) => r && r.id === row.id);
  const next = {
    id: row.id,
    name: row.name ?? "",
    country: row.country ?? (data[idx]?.country || ""),
  };
  if (idx >= 0) {
    data[idx] = next;
  } else {
    data.push(next);
  }
}
